import { Injectable, TemplateRef, Type, inject, isDevMode } from '@angular/core';
import { Dialog, DialogConfig, DialogRef } from '@angular/cdk/dialog';
import { Directionality } from '@angular/cdk/bidi';
import { Overlay } from '@angular/cdk/overlay';
import { CMP_DIALOG_CHROME, CmpDialogContainer, DialogChrome } from './dialog-container';
import { nextDialogIds } from './dialog-id';
import { CompsychDialogRef } from './dialog-ref';
import { DialogVariant } from './dialog-types';

/** Configuration for {@link DialogService.open}. */
export interface CompsychDialogConfig<D = unknown> {
  /** Layout type. Defaults to `modal-leading-icon`. */
  variant?: DialogVariant;
  /** Heading text (drives the accessible name via `aria-labelledby`). */
  title?: string;
  /** Supporting text (rendered for `centered` / `alert` variants). */
  description?: string;
  /** Lucide icon name (leading header icon / centered badge glyph). */
  icon?: string;
  /** When true (default) shows a close button and closes on Escape / backdrop click. */
  dismissible?: boolean;
  /** Accessible label for the close button. */
  closeLabel?: string;
  /** Accessible name used when no `title` is provided. */
  ariaLabel?: string;
  /** Width override — any CSS length. Defaults to the variant width, viewport-clamped. */
  width?: string;
  /** Whether to render the projected actions footer. */
  showActions?: boolean;
  /** Data injected into the opened component via the CDK `DIALOG_DATA` token. */
  data?: D;
  /** Extra class(es) for the overlay pane. */
  panelClass?: string | string[];
}

/**
 * Opens a component or template as a modal dialog inside the compsych-dialog chrome,
 * imperatively (like Angular Material's `MatDialog`). Built on `@angular/cdk/dialog`.
 *
 * The opened content is placed in the dialog body; the header (icon/title/close) and
 * behavior come from the config. The opened component can inject the CDK `DialogRef`
 * (to close, optionally with a result) and `DIALOG_DATA` (to read `config.data`) — both
 * re-exported from `@compsych-ui-components/angular`.
 *
 *   const ref = dialogService.open(MyComponent, {
 *     title: 'Edit details', variant: 'modal-leading-icon', icon: 'globe', data: { id },
 *   });
 *   ref.closed.subscribe((result) => { ... });
 *
 * Requires `@angular/cdk/overlay-prebuilt.css` + the `@compsych-ui-components/shared` tokens
 * loaded globally. The scrim color is injected by the dialog — no extra stylesheet needed.
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly dialog = inject(Dialog);
  private readonly overlay = inject(Overlay);
  private readonly directionality = inject(Directionality);

  /** Opens `content` as a modal dialog. Returns a {@link CompsychDialogRef} (close / closed). */
  open<R = unknown, D = unknown, C = unknown>(
    content: Type<C> | TemplateRef<C>,
    config: CompsychDialogConfig<D> = {},
  ): CompsychDialogRef<R> {
    // Dev-only guardrail (parity with the declarative component): no title + no ariaLabel
    // means the dialog has no accessible name (WCAG 2.2 §4.1.2).
    if (isDevMode() && !config.title && !config.ariaLabel) {
      console.warn(
        '[compsych-dialog] Opened via DialogService with no accessible name — set `title` ' +
          'or `ariaLabel` so assistive technology can identify the dialog (WCAG 2.2 §4.1.2).',
      );
    }

    const ids = nextDialogIds();
    const chrome: DialogChrome = {
      variant: config.variant ?? 'modal-leading-icon',
      title: config.title ?? '',
      description: config.description ?? '',
      icon: config.icon ?? '',
      dismissible: config.dismissible ?? true,
      closeLabel: config.closeLabel ?? 'Close dialog',
      width: config.width ?? '',
      showActions: config.showActions ?? true,
      titleId: ids.titleId,
      descId: ids.descId,
    };

    let ref: CompsychDialogRef<R> | undefined;
    const cdkConfig = {
      // Object form so chrome params reach the container via DI (not spread onto the
      // config, which would clobber real fields like `width`).
      container: {
        type: CmpDialogContainer,
        providers: () => [{ provide: CMP_DIALOG_CHROME, useValue: chrome }],
      },
      hasBackdrop: true,
      backdropClass: 'cmp-dialog-scrim',
      panelClass: config.panelClass ?? 'cmp-dialog-pane',
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      direction: this.directionality.value,
      role: chrome.variant === 'alert' ? ('alertdialog' as const) : ('dialog' as const),
      // dismissible → CDK closes on Escape / backdrop; non-dismissible → forced choice.
      disableClose: !chrome.dismissible,
      autoFocus: 'dialog',
      restoreFocus: true,
      ariaModal: true,
      ariaLabelledBy: chrome.title ? chrome.titleId : null,
      ariaDescribedBy:
        chrome.variant !== 'modal-leading-icon' && chrome.description ? chrome.descId : null,
      ariaLabel: !chrome.title ? config.ariaLabel || null : null,
      data: config.data,
      // Wrap the CDK ref (async-safe `closed`) and expose it to the opened content via DI,
      // so the content can inject `CompsychDialogRef` to close with a result.
      providers: (cdkRef: DialogRef<R, C>) => {
        ref = new CompsychDialogRef<R>(cdkRef as DialogRef<R, unknown>);
        return [{ provide: CompsychDialogRef, useValue: ref }];
      },
    } as DialogConfig<D, DialogRef<R, C>>;

    const cdkRef = this.dialog.open<R, D, C>(content, cdkConfig);
    // `providers` runs synchronously during open(), so `ref` is set; fall back defensively.
    return ref ?? new CompsychDialogRef<R>(cdkRef as DialogRef<R, unknown>);
  }
}
